/**
 * Page setup of a document or template: orientation, margins, and the header and footer bands.
 *
 * Stored as `page_setup jsonb` on `templates` and `generated_documents` (null = defaults). A
 * document generated from a template starts with a copy of the template's page setup and can
 * change its own afterwards. Paper size is NOT part of it: that stays a per-browser preference
 * (src/utils/paperSize.js).
 *
 * Shape (version 1):
 *   {
 *     version: 1,
 *     orientation: 'portrait' | 'landscape',
 *     margins: 'normal' | 'narrow' | 'wide',          // 1 in, 0.5 in, 1.25 in
 *     differentFirstPage: boolean,
 *     header: Band, footer: Band,                     // every page (pages 2+ with differentFirstPage)
 *     firstHeader: Band | null, firstFooter: Band | null,   // page 1 with differentFirstPage; null = empty
 *   }
 *   Band = { left: Slot, center: Slot, right: Slot }
 *   Slot = { type: 'none' }
 *        | { type: 'text', text }                     // max 200 chars, single line, live fields:
 *                                                     // {title} {customer} {project} {date} {page} {pages}
 *        | { type: 'image', src, alt, height }        // src: Quill's Supabase storage only; 16 to 96 px
 *
 * page_setup is data, never HTML. Every render path must:
 *   - pass the stored value through `normalizePageSetup` (drops unknown keys, clamps numbers,
 *     strips control characters, caps text, rejects image URLs that are not Quill's storage);
 *   - escape slot text for its target (`renderSlotHtml` for HTML, `slotTextToCssContent` for the
 *     print window's @page margin boxes, `tokenizeSlotText` for Word fields);
 *   - check image URLs again with `isAllowedImageUrl` right before rendering them.
 *
 * Pure module: no DOM, no storage, no network.
 */

import { PX_PER_INCH, getPaperSize } from './paperSize';

export const PAGE_SETUP_VERSION = 1;

export const ORIENTATIONS = Object.freeze(['portrait', 'landscape']);

/** Margin presets (every side). */
export const MARGIN_PRESETS = Object.freeze({
  normal: Object.freeze({ in: 1, twips: 1440 }),
  narrow: Object.freeze({ in: 0.5, twips: 720 }),
  wide: Object.freeze({ in: 1.25, twips: 1800 }),
});

/** Margin preset ids in menu order. */
export const MARGIN_IDS = Object.freeze(['normal', 'narrow', 'wide']);

/** Slot positions in a band, left to right. */
export const SLOT_POSITIONS = Object.freeze(['left', 'center', 'right']);

export const SLOT_TYPES = Object.freeze(['none', 'text', 'image']);

/** Longest slot text (characters, after control characters are removed). */
export const SLOT_TEXT_MAX = 200;
/** Longest image alt text. */
export const IMAGE_ALT_MAX = 200;
/** Longest accepted image URL. */
export const IMAGE_URL_MAX = 2048;
export const IMAGE_HEIGHT_MIN = 16;
export const IMAGE_HEIGHT_MAX = 96;
export const IMAGE_HEIGHT_DEFAULT = 32;

/** Live fields a text slot can contain, as `{name}`. */
export const FIELD_TOKENS = Object.freeze(['title', 'customer', 'project', 'date', 'page', 'pages']);

/** Text for the "Page number" shortcut. */
export const PAGE_NUMBER_TEXT = Object.freeze({ simple: '{page}', ofTotal: 'Page {page} of {pages}' });

/** Quill's Supabase project (hxxkousbqstbeuujjyot). Header/footer images must live in its storage. */
export const ALLOWED_IMAGE_ORIGIN = 'https://hxxkousbqstbeuujjyot.supabase.co';
/** Path prefix of Supabase storage objects (public, sign and authenticated routes). */
export const ALLOWED_IMAGE_PATH_PREFIX = '/storage/v1/object/';

const deepFreeze = (value) => {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }
  return value;
};

const noneSlot = () => ({ type: 'none' });
const emptyBand = () => ({ left: noneSlot(), center: noneSlot(), right: noneSlot() });

/** A slot with nothing in it. */
export const EMPTY_SLOT = deepFreeze(noneSlot());
/** A band with nothing in it. */
export const EMPTY_BAND = deepFreeze(emptyBand());

/**
 * Build the default page setup (fresh, mutable).
 * @returns {object} Defaults: portrait, normal margins, no header, centered page number footer.
 */
const buildDefaults = () => ({
  version: PAGE_SETUP_VERSION,
  orientation: 'portrait',
  margins: 'normal',
  differentFirstPage: false,
  header: emptyBand(),
  footer: { left: noneSlot(), center: { type: 'text', text: PAGE_NUMBER_TEXT.simple }, right: noneSlot() },
  firstHeader: null,
  firstFooter: null,
});

/**
 * Page setup used when a row's page_setup is null: no header, the page number centered in the
 * footer (what the Pages view showed before page setup existed), portrait, normal margins.
 */
export const DEFAULT_PAGE_SETUP = deepFreeze(buildDefaults());

const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
const has = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// C0/C1 controls and DEL, plus bidi embedding/override/isolate controls (they can visually
// reorder band text), zero-width and line/paragraph separators, BOM.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F\u200B-\u200F\u2028-\u202E\u2060-\u2069\uFEFF]/g;

/**
 * Remove control characters (whitespace controls such as tabs and newlines become spaces: band
 * text is a single line) and cap the length without splitting a surrogate pair.
 * @param {unknown} value - Raw text.
 * @param {number} max - Longest result (code points).
 * @returns {string} Clean text ('' for non-strings).
 */
export function cleanText(value, max) {
  if (typeof value !== 'string') return '';
  const cleaned = value.replace(/[\t\n\r\v\f]/g, ' ').replace(CONTROL_CHARS, '');
  const chars = Array.from(cleaned);
  return chars.length > max ? chars.slice(0, max).join('') : cleaned;
}

/**
 * Whether a URL may be shown as a header/footer image: an https object URL in Quill's own
 * Supabase storage (`https://hxxkousbqstbeuujjyot.supabase.co/storage/v1/object/...`). data:,
 * blob:, http: and any other host are rejected, as are URLs with credentials, a port, quotes,
 * angle brackets, backslashes, whitespace or control characters (so an accepted URL is also safe
 * inside a quoted HTML attribute or CSS `url("...")`).
 * @param {unknown} url - Candidate URL.
 * @returns {boolean} True when allowed.
 */
export function isAllowedImageUrl(url) {
  if (typeof url !== 'string' || url.length === 0 || url.length > IMAGE_URL_MAX) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\s\u0000-\u001F\u007F"'`<>\\]/.test(url)) return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  if (parsed.origin !== ALLOWED_IMAGE_ORIGIN) return false;
  if (parsed.username || parsed.password || parsed.port) return false;
  // The raw string must start with the origin too (no `https://evil@host`-style tricks that the
  // parser would rewrite, no uppercase scheme games); `new URL` already resolved dot segments.
  if (!url.toLowerCase().startsWith(`${ALLOWED_IMAGE_ORIGIN}${ALLOWED_IMAGE_PATH_PREFIX}`)) return false;
  if (!parsed.pathname.startsWith(ALLOWED_IMAGE_PATH_PREFIX)) return false;
  const segments = parsed.pathname.slice(ALLOWED_IMAGE_PATH_PREFIX.length).split('/');
  // A route or bucket plus an object name, no empty, dot or encoded-separator segments.
  if (segments.length < 2) return false;
  if (segments.some((segment) => segment === '' || segment === '.' || segment === '..' || /%2e|%2f|%5c/i.test(segment))) return false;
  return true;
}

/**
 * Clamp an image height to 16 to 96 px (whole pixels; junk becomes the default 32).
 * @param {unknown} value - Raw height.
 * @returns {number} Height (px).
 */
export function clampImageHeight(value) {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value;
  if (typeof n !== 'number' || !Number.isFinite(n)) return IMAGE_HEIGHT_DEFAULT;
  return Math.min(IMAGE_HEIGHT_MAX, Math.max(IMAGE_HEIGHT_MIN, Math.round(n)));
}

/**
 * Normalize one slot. Unknown types, and images whose URL is not allowed, become `{type:'none'}`.
 * @param {unknown} value - Raw slot.
 * @returns {{type: 'none'}|{type: 'text', text: string}|{type: 'image', src: string, alt: string, height: number}} Slot.
 */
export function normalizeSlot(value) {
  if (!isPlainObject(value)) return noneSlot();
  if (value.type === 'text') {
    return { type: 'text', text: cleanText(value.text, SLOT_TEXT_MAX) };
  }
  if (value.type === 'image') {
    if (!isAllowedImageUrl(value.src)) return noneSlot();
    return {
      type: 'image',
      src: value.src,
      alt: cleanText(value.alt, IMAGE_ALT_MAX),
      height: clampImageHeight(value.height),
    };
  }
  return noneSlot();
}

/**
 * Normalize one band (missing slots are empty).
 * @param {unknown} value - Raw band.
 * @returns {{left: object, center: object, right: object}} Band.
 */
export function normalizeBand(value) {
  const source = isPlainObject(value) ? value : {};
  return {
    left: normalizeSlot(source.left),
    center: normalizeSlot(source.center),
    right: normalizeSlot(source.right),
  };
}

/**
 * Whether a slot shows nothing (none, or text that is only spaces).
 * @param {unknown} slot - Slot (normalized or raw).
 * @returns {boolean} True when empty.
 */
export function isSlotEmpty(slot) {
  const s = normalizeSlot(slot);
  if (s.type === 'text') return s.text.trim() === '';
  return s.type === 'none';
}

/**
 * Whether a band shows nothing.
 * @param {unknown} band - Band (normalized or raw, null allowed).
 * @returns {boolean} True when every slot is empty.
 */
export function isBandEmpty(band) {
  if (!isPlainObject(band)) return true;
  return SLOT_POSITIONS.every((position) => isSlotEmpty(band[position]));
}

/**
 * Validate and normalize a stored page setup. Always returns a complete, fresh object (null,
 * junk and older shapes become the defaults); never throws. Unknown keys are dropped.
 *
 * `header` / `footer`: a missing key means the default band (no header; page number footer);
 * an explicit null or junk means an empty band. `firstHeader` / `firstFooter`: missing, junk, or a
 * band whose three slots are all None becomes null (= nothing on the first page).
 *
 * @param {unknown} value - Raw page_setup (object, JSON string, null...).
 * @returns {object} Normalized page setup.
 */
export function normalizePageSetup(value) {
  let source = value;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      source = null;
    }
  }
  const defaults = buildDefaults();
  if (!isPlainObject(source)) return defaults;
  const orientation = typeof source.orientation === 'string' ? source.orientation.trim().toLowerCase() : '';
  const margins = typeof source.margins === 'string' ? source.margins.trim().toLowerCase() : '';
  const firstBand = (raw) => {
    if (!isPlainObject(raw)) return null;
    const band = normalizeBand(raw);
    return SLOT_POSITIONS.every((position) => band[position].type === 'none') ? null : band;
  };
  return {
    version: PAGE_SETUP_VERSION,
    orientation: ORIENTATIONS.includes(orientation) ? orientation : defaults.orientation,
    margins: MARGIN_IDS.includes(margins) ? margins : defaults.margins,
    differentFirstPage: source.differentFirstPage === true,
    header: has(source, 'header') && source.header !== undefined ? normalizeBand(source.header) : defaults.header,
    footer: has(source, 'footer') && source.footer !== undefined ? normalizeBand(source.footer) : defaults.footer,
    firstHeader: firstBand(source.firstHeader),
    firstFooter: firstBand(source.firstFooter),
  };
}

/**
 * Stable string form of a page setup (normalized first), e.g. for "changed?" checks and the
 * autosave baseline. Two equivalent setups give the same string.
 * @param {unknown} value - Page setup.
 * @returns {string} JSON.
 */
export function serializePageSetup(value) {
  return JSON.stringify(normalizePageSetup(value));
}

/**
 * Whether a page setup equals the defaults (a null page_setup).
 * @param {unknown} value - Page setup.
 * @returns {boolean} True when it renders like the defaults.
 */
export function isDefaultPageSetup(value) {
  return serializePageSetup(value) === serializePageSetup(null);
}

/**
 * A copy of a template's page_setup for a new document generated from it (null stays null, so
 * the document uses the defaults; anything else is normalized).
 * @param {unknown} value - The template's page_setup.
 * @returns {object|null} Value for the new row's page_setup.
 */
export function pageSetupForNewDocument(value) {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'string' ? normalizePageSetupOrNull(value) : value;
  return isPlainObject(parsed) ? normalizePageSetup(parsed) : null;
}

const normalizePageSetupOrNull = (text) => {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

/**
 * Whether a PostgREST/Postgres error says the `page_setup` column does not exist yet (the
 * 20261105000100_page_setup migration is not applied): 42703 from Postgres or PGRST204 from the
 * schema cache, naming the column.
 * @param {unknown} error - Error from a query.
 * @returns {boolean} True for a missing page_setup column.
 */
export function isMissingPageSetupColumn(error) {
  if (!error || typeof error !== 'object') return false;
  const code = String(error.code || '');
  if (code && code !== '42703' && code !== 'PGRST204') return false;
  const text = `${error.message || ''} ${error.details || ''} ${error.hint || ''}`;
  return /page_setup/.test(text) && /column|schema cache/i.test(text);
}

let warnedMissingPageSetup = false;
/**
 * Log once per page load that page setup was not saved because the column is missing.
 * @returns {void}
 */
export function warnMissingPageSetupColumn() {
  if (warnedMissingPageSetup) return;
  warnedMissingPageSetup = true;
  console.warn('The page_setup column is missing (migration 20261105000100_page_setup.sql is not applied): page setup was not saved.');
}

/**
 * Page and content geometry for a page setup on a paper size, for the Pages view and the
 * exporters.
 *
 * px values are CSS px at 96 per inch. `width`/`height` are the sheet as it lies (landscape
 * swaps them). `bandOffset` is the distance from the sheet edge to the header/footer band (half
 * the margin, Word's 0.5 in for normal margins).
 *
 * `twips.width`/`twips.height` are the sheet as it lies too; for the docx library pass
 * `twips.pageSize` (portrait width/height plus orientation: docx swaps them itself for
 * landscape). `twips.margin.header`/`footer` are the docx header/footer distances.
 *
 * @param {unknown} pageSetup - Page setup (normalized or raw).
 * @param {unknown} paperSizeId - Paper size id ('letter' | 'a4'; unknown = Letter).
 * @returns {Readonly<{paperSize: string, orientation: string, margins: string, width: number, height: number, margin: {top: number, right: number, bottom: number, left: number}, marginIn: number, contentWidth: number, contentHeight: number, bandOffset: number, cssSize: string, cssMargin: string, twips: object}>} Geometry.
 */
export function resolvePageGeometry(pageSetup, paperSizeId) {
  const setup = normalizePageSetup(pageSetup);
  const paper = getPaperSize(paperSizeId);
  const landscape = setup.orientation === 'landscape';
  const preset = MARGIN_PRESETS[setup.margins];
  const marginPx = Math.round(preset.in * PX_PER_INCH);
  const width = landscape ? paper.height : paper.width;
  const height = landscape ? paper.width : paper.height;
  const twipsWidth = landscape ? paper.twips.height : paper.twips.width;
  const twipsHeight = landscape ? paper.twips.width : paper.twips.height;
  const bandTwips = Math.round(preset.twips / 2);
  return deepFreeze({
    paperSize: paper.id,
    orientation: setup.orientation,
    margins: setup.margins,
    width,
    height,
    margin: { top: marginPx, right: marginPx, bottom: marginPx, left: marginPx },
    marginIn: preset.in,
    contentWidth: width - 2 * marginPx,
    contentHeight: height - 2 * marginPx,
    bandOffset: Math.round(marginPx / 2),
    cssSize: `${paper.cssSize} ${setup.orientation}`,
    cssMargin: `${preset.in}in`,
    twips: {
      width: twipsWidth,
      height: twipsHeight,
      pageSize: { width: paper.twips.width, height: paper.twips.height, orientation: setup.orientation },
      margin: {
        top: preset.twips,
        right: preset.twips,
        bottom: preset.twips,
        left: preset.twips,
        header: bandTwips,
        footer: bandTwips,
      },
    },
  });
}

/**
 * Header and footer bands for one page (1-based). With `differentFirstPage`, page 1 uses
 * `firstHeader` / `firstFooter` (null = an empty band).
 * @param {unknown} pageSetup - Page setup (normalized or raw).
 * @param {number} pageNumber - Page number (1-based; junk counts as page 1).
 * @returns {{header: object, footer: object, isFirstPage: boolean, usesFirstPageBands: boolean}} Bands (fresh objects).
 */
export function bandsForPage(pageSetup, pageNumber) {
  const setup = normalizePageSetup(pageSetup);
  const page = Number.isFinite(pageNumber) && pageNumber >= 1 ? Math.floor(pageNumber) : 1;
  const isFirstPage = page === 1;
  if (isFirstPage && setup.differentFirstPage) {
    return {
      header: setup.firstHeader || emptyBand(),
      footer: setup.firstFooter || emptyBand(),
      isFirstPage,
      usesFirstPageBands: true,
    };
  }
  return { header: setup.header, footer: setup.footer, isFirstPage, usesFirstPageBands: false };
}

/**
 * Format a date as a long date in the viewer's locale (e.g. "November 4, 2026").
 * @param {unknown} value - Date, ISO string or epoch ms.
 * @param {string|string[]} [locale] - Locale (default: the browser's).
 * @returns {string} Formatted date, or '' when the value is not a date.
 */
export function formatFieldDate(value, locale) {
  if (value === null || value === undefined || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(locale || undefined, { dateStyle: 'long' }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'long' }).format(date);
  }
}

/**
 * Live-field context for a document or template (page / pages are added per sheet).
 * @param {{title?: string, customer?: string, project?: string, createdAt?: unknown, locale?: string}} input - Values.
 * @returns {{title: string, customer: string, project: string, date: string}} Context.
 */
export function buildFieldContext({ title, customer, project, createdAt, locale } = {}) {
  return {
    title: cleanText(title, 500),
    customer: cleanText(customer, 500),
    project: cleanText(project, 500),
    date: formatFieldDate(createdAt, locale),
  };
}

const TOKEN_PATTERN = /\{(title|customer|project|date|page|pages)\}/g;

const contextValue = (ctx, key) => {
  const raw = ctx && Object.prototype.hasOwnProperty.call(ctx, key) ? ctx[key] : undefined;
  if (raw === null || raw === undefined) return '';
  if (typeof raw === 'number') return Number.isFinite(raw) ? String(raw) : '';
  return typeof raw === 'string' ? cleanText(raw, 500) : '';
};

/**
 * Split slot text into literal text and fields. Fields other than {page} / {pages} are resolved
 * from `ctx`; with `keepPageFields`, {page} and {pages} stay as `{type:'page'}` / `{type:'pages'}`
 * parts (Word PageNumber fields, CSS counters) instead of being resolved from `ctx.page` /
 * `ctx.pages`. Adjacent text parts are merged; empty text parts are dropped.
 * @param {unknown} text - Slot text.
 * @param {object} [ctx] - {title, customer, project, date, page, pages}.
 * @param {{keepPageFields?: boolean}} [options] - Options.
 * @returns {Array<{type: 'text', text: string}|{type: 'page'}|{type: 'pages'}>} Parts.
 */
export function tokenizeSlotText(text, ctx = {}, { keepPageFields = false } = {}) {
  const source = cleanText(text, SLOT_TEXT_MAX);
  const parts = [];
  const pushText = (value) => {
    if (!value) return;
    const last = parts[parts.length - 1];
    if (last && last.type === 'text') last.text += value;
    else parts.push({ type: 'text', text: value });
  };
  let index = 0;
  for (const match of source.matchAll(TOKEN_PATTERN)) {
    const key = match[1];
    pushText(source.slice(index, match.index));
    if (keepPageFields && (key === 'page' || key === 'pages')) parts.push({ type: key });
    else pushText(contextValue(ctx, key));
    index = match.index + match[0].length;
  }
  pushText(source.slice(index));
  return parts;
}

/**
 * Resolve the live fields in slot text: {title} {customer} {project} {date} {page} {pages}.
 * Unknown `{...}` stays as typed; a field with no value becomes ''. The result is PLAIN TEXT:
 * escape it for its target (`renderSlotHtml` for HTML).
 * @param {unknown} text - Slot text.
 * @param {object} [ctx] - {title, customer, project, date, page, pages}.
 * @returns {string} Text.
 */
export function renderSlotText(text, ctx = {}) {
  return tokenizeSlotText(text, ctx).map((part) => part.text).join('');
}

/**
 * Escape text for HTML element content or a quoted attribute value.
 * @param {unknown} value - Text.
 * @returns {string} Escaped text.
 */
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * `renderSlotText` escaped for HTML.
 * @param {unknown} text - Slot text.
 * @param {object} [ctx] - Field context.
 * @returns {string} HTML-safe text.
 */
export function renderSlotHtml(text, ctx = {}) {
  return escapeHtml(renderSlotText(text, ctx));
}

/**
 * Quote text as a CSS string literal, safe inside a <style> element: backslash, quotes and
 * newlines are escaped, and `<` / `>` / `&` become hex escapes so the text can never close the
 * style element.
 * @param {unknown} value - Text.
 * @returns {string} Double-quoted CSS string.
 */
export function cssString(value) {
  // eslint-disable-next-line no-control-regex
  const escaped = String(value ?? '').replace(/[\\"'<>&\n\r\f\u0000-\u001F\u007F]/g, (ch) => `\\${ch.charCodeAt(0).toString(16)} `);
  return `"${escaped}"`;
}

/**
 * Slot text as a CSS `content:` value for @page margin boxes: literal text as quoted strings,
 * {page} / {pages} as `counter(page)` / `counter(pages)`, other fields resolved from `ctx`.
 * With `maxWidth`, the text is cut to fit with an ellipsis ({@link fitSlotParts}): Chrome does
 * not clip margin box content, so long text would run over the next slot and the paper edge.
 * @param {unknown} text - Slot text.
 * @param {object} [ctx] - Field context.
 * @param {{maxWidth?: number, fontPx?: number}} [options] - Width to fit (px; 0 = no limit) and font size.
 * @returns {string} CSS value ('""' when empty).
 */
export function slotTextToCssContent(text, ctx = {}, { maxWidth = 0, fontPx = 11 } = {}) {
  const tokens = tokenizeSlotText(text, ctx, { keepPageFields: true });
  const parts = fitSlotParts(tokens, maxWidth, fontPx).map((part) => {
    if (part.type === 'page') return 'counter(page)';
    if (part.type === 'pages') return 'counter(pages)';
    return cssString(part.text);
  });
  return parts.length ? parts.join(' ') : '""';
}

/* ------------------------------------------------------------------------------------------ */
/* Band layout shared by the Pages view, the dialog preview and every export                    */
/* ------------------------------------------------------------------------------------------ */

/**
 * Space a band keeps between its content and the text column (px). A band fills its margin; its
 * content is centred in what is left once this and {@link BAND_EDGE_INSET_PX} are taken off.
 */
export const BAND_INSET_PX = 8;
/**
 * Space a band keeps between its content and the paper edge (px): 16 px is 0.17 in (12 pt),
 * about the edge most printers can print to, so a logo in Narrow margins is never clipped.
 * The header takes it at its top, the footer at its bottom (the Pages view's band padding, the
 * PDF margin boxes' padding and the Word header / footer distance all use it).
 */
export const BAND_EDGE_INSET_PX = 16;
/** Gap between the three slots of a band (px). */
export const BAND_COLUMN_GAP_PX = 12;

/**
 * Tallest a band image is drawn, whatever its stored height: the margin minus
 * {@link BAND_EDGE_INSET_PX} and {@link BAND_INSET_PX} (Normal 72 px, Narrow 24 px, Wide 96 px).
 * @param {{margin: {top: number}}} geometry - `resolvePageGeometry(...)`.
 * @returns {number} Height (px).
 */
export function bandImageMaxHeight(geometry) {
  const margin = Number(geometry?.margin?.top) || 0;
  return Math.max(IMAGE_HEIGHT_MIN, Math.min(IMAGE_HEIGHT_MAX, Math.floor(margin - BAND_EDGE_INSET_PX - BAND_INSET_PX)));
}

/**
 * Whether only a band's centre slot shows something (it then takes the whole column).
 * @param {unknown} band - Band.
 * @returns {boolean} True when left and right are empty and the centre is not.
 */
export function bandSpansCenter(band) {
  if (!isPlainObject(band)) return false;
  return !isSlotEmpty(band.center) && isSlotEmpty(band.left) && isSlotEmpty(band.right);
}

/**
 * Width one slot of a band can use (px): a third of the column less the gaps, or the whole
 * column when only the centre slot is used.
 * @param {{contentWidth: number}} geometry - `resolvePageGeometry(...)`.
 * @param {boolean} span - {@link bandSpansCenter}.
 * @returns {number} Width (px).
 */
export function bandSlotWidth(geometry, span) {
  const column = Math.max(0, Number(geometry?.contentWidth) || 0);
  if (span) return column;
  return Math.max(0, Math.floor((column - 2 * BAND_COLUMN_GAP_PX) / 3));
}

/**
 * The size a band image is drawn at: its stored height, but never taller than the band
 * ({@link bandImageMaxHeight}) nor wider than its slot ({@link bandSlotWidth}), keeping the
 * image's aspect ratio. Without the natural size only the height is known (CSS keeps the width
 * inside the slot).
 * @param {object} params - Inputs.
 * @param {number} params.height - Stored slot height (px).
 * @param {number} [params.naturalWidth] - Image width.
 * @param {number} [params.naturalHeight] - Image height.
 * @param {object} params.geometry - `resolvePageGeometry(...)`.
 * @param {boolean} [params.span] - Only the centre slot is used.
 * @returns {{width: number|null, height: number}} Size (px).
 */
export function bandImageBox({ height, naturalWidth, naturalHeight, geometry, span = false }) {
  let h = Math.min(clampImageHeight(height), bandImageMaxHeight(geometry));
  if (!(naturalWidth > 0 && naturalHeight > 0)) return { width: null, height: h };
  let w = (h * naturalWidth) / naturalHeight;
  const maxWidth = bandSlotWidth(geometry, span);
  if (maxWidth > 0 && w > maxWidth) {
    w = maxWidth;
    h = (w * naturalHeight) / naturalWidth;
  }
  return { width: Math.max(1, Math.round(w)), height: Math.max(1, Math.round(h)) };
}

/** Width of an Arial glyph as a share of the font size, by character class (approximate). */
const glyphShare = (ch) => {
  if (ch === ' ') return 0.278;
  if (/[il.,:;'|!`]/.test(ch)) return 0.278;
  if (/[fjrtI()[\]{}\-/\\"]/.test(ch)) return 0.36;
  if (/[mwMW@%]/.test(ch)) return 0.89;
  if (/[A-Z]/.test(ch)) return 0.7;
  if (/[0-9a-z]/.test(ch)) return 0.556;
  return 0.75; // other scripts and symbols: err on the wide side
};

/**
 * Estimated width of band text in Arial (px). Deliberately on the wide side (+8%), so text
 * cut to fit never overflows its slot; used where the renderer cannot clip (Word paragraphs,
 * Chrome's @page margin boxes, which ignore overflow and text-overflow).
 * @param {string} text - Text.
 * @param {number} fontPx - Font size (px).
 * @returns {number} Width (px).
 */
export function estimateBandTextWidth(text, fontPx) {
  let share = 0;
  for (const ch of String(text || '')) share += glyphShare(ch);
  return share * fontPx * 1.08;
}

/** Room a page number field takes in the width estimate ({page} as up to 3 digits, "2-4" too). */
const PAGE_FIELD_TEXT = '888';
/** Ellipsis appended to text cut to fit. */
export const BAND_ELLIPSIS = '…';

/**
 * Cut tokenized slot text (see {@link tokenizeSlotText}) to fit a width, ending with an
 * ellipsis, like the Pages view's single line with text-overflow: ellipsis. Page fields are
 * kept whole (counted as three digits) or dropped once nothing fits.
 * @param {Array<{type: string, text?: string}>} parts - Parts.
 * @param {number} maxWidth - Width (px); 0 or less = no limit.
 * @param {number} fontPx - Font size (px).
 * @returns {Array<{type: string, text?: string}>} Parts that fit (fresh objects).
 */
export function fitSlotParts(parts, maxWidth, fontPx) {
  const list = Array.isArray(parts) ? parts : [];
  if (!(maxWidth > 0)) return list.map((part) => ({ ...part }));
  const widthOf = (part) => estimateBandTextWidth(part.type === 'text' ? part.text : PAGE_FIELD_TEXT, fontPx);
  const total = list.reduce((sum, part) => sum + widthOf(part), 0);
  if (total <= maxWidth) return list.map((part) => ({ ...part }));
  const budget = maxWidth - estimateBandTextWidth(BAND_ELLIPSIS, fontPx);
  const out = [];
  let used = 0;
  for (const part of list) {
    const width = widthOf(part);
    if (used + width <= budget) {
      out.push({ ...part });
      used += width;
      continue;
    }
    if (part.type === 'text') {
      let cut = '';
      for (const ch of part.text) {
        const next = used + estimateBandTextWidth(cut + ch, fontPx);
        if (next > budget) break;
        cut += ch;
      }
      cut = cut.replace(/\s+$/, '');
      if (cut) out.push({ type: 'text', text: cut });
    }
    break;
  }
  const last = out[out.length - 1];
  if (last && last.type === 'text') last.text += BAND_ELLIPSIS;
  else out.push({ type: 'text', text: BAND_ELLIPSIS });
  return out;
}
